import React, { useEffect, useMemo, useState } from "react";
import { Trash2, Plus, Search } from "lucide-react";

export default function InovasiTransaction() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  const [meta, setMeta] = useState({
    total_pages: 1,
    total_data: 0,
  });

  const [filters, setFilters] = useState({
    search: "",
    customer_id: "",
    status: "",
    date_from: "",
    date_to: "",
  });

  const token = localStorage.getItem("token") || "";

  const qs = useMemo(() => {
    return new URLSearchParams({
      page,
      limit,
      ...Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== "")
      ),
    }).toString();
  }, [page, limit, filters]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const res = await fetch(
        `https://api.wifikamu.my.id/transaction-inovasi?${qs}`,
        {
          headers: {
            Authorization: token,
          },
        }
      );

      const json = await res.json();

      setRows(json.data || []);
      setMeta(
        json.pagination || {
          total_pages: 1,
          total_data: 0,
        }
      );
    } catch (error) {
      console.error("Fetch Error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [qs]);

  const onChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
    });
    setPage(1);
  };

  const reset = () => {
    setFilters({
      search: "",
      customer_id: "",
      status: "",
      date_from: "",
      date_to: "",
    });
    setPage(1);
  };

  const del = async (id) => {
    if (!window.confirm("Delete data ini?")) return;

    try {
      await fetch(`https://api.wifikamu.my.id/transaction-inovasi/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: token,
        },
      });

      fetchData();
    } catch (error) {
      console.error("Delete Error:", error);
    }
  };

  return (
    <div className="min-h-screen p-6 space-y-6 bg-white dark:bg-slate-900 text-slate-900 dark:text-white">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <h1 className="text-2xl font-bold">Inovasi Transaction</h1>

        <button className="flex items-center gap-2 px-4 py-2 text-white bg-blue-600 rounded-xl hover:bg-blue-700">
          <Plus size={18} />
          Add Data
        </button>
      </div>

      {/* Filter */}
      <div className="grid grid-cols-1 gap-3 p-4 border md:grid-cols-6 rounded-2xl border-slate-200 dark:border-slate-700">
        <input
          type="text"
          name="search"
          placeholder="Search Invoice"
          value={filters.search}
          onChange={onChange}
          className="px-3 py-2 border rounded-xl bg-transparent"
        />

        <input
          type="text"
          name="customer_id"
          placeholder="Customer ID"
          value={filters.customer_id}
          onChange={onChange}
          className="px-3 py-2 border rounded-xl bg-transparent"
        />

        <input
          type="text"
          name="status"
          placeholder="Status"
          value={filters.status}
          onChange={onChange}
          className="px-3 py-2 border rounded-xl bg-transparent"
        />

        <input
          type="date"
          name="date_from"
          value={filters.date_from}
          onChange={onChange}
          className="px-3 py-2 border rounded-xl bg-transparent"
        />

        <input
          type="date"
          name="date_to"
          value={filters.date_to}
          onChange={onChange}
          className="px-3 py-2 border rounded-xl bg-transparent"
        />

        <div className="flex gap-2">
          <button
            onClick={() => setPage(1)}
            className="flex items-center justify-center flex-1 gap-2 px-3 py-2 text-white rounded-xl bg-slate-800 dark:bg-slate-200 dark:text-black"
          >
            <Search size={16} />
            Filter
          </button>

          <button onClick={reset} className="px-3 py-2 border rounded-xl">
            Reset
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="p-4 border rounded-2xl border-slate-200 dark:border-slate-700">
          <p className="text-sm opacity-70">Total Transaksi</p>
          <h2 className="text-2xl font-bold">{meta.total_data}</h2>
        </div>

        <div className="p-4 border rounded-2xl border-slate-200 dark:border-slate-700">
          <p className="text-sm opacity-70">Total Nominal Halaman</p>
          <h2 className="text-2xl font-bold">
            {rows
              .reduce((acc, item) => acc + Number(item.total || 0), 0)
              .toLocaleString()}
          </h2>
        </div>

        <div className="p-4 border rounded-2xl border-slate-200 dark:border-slate-700">
          <p className="text-sm opacity-70">Status</p>
          <h2 className="text-2xl font-bold">Live</h2>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto border rounded-2xl border-slate-200 dark:border-slate-700">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 dark:bg-slate-800">
            <tr>
              <th className="p-3 text-left">ID</th>
              <th className="p-3 text-left">Customer</th>
              <th className="p-3 text-left">Invoice</th>
              <th className="p-3 text-left">Date</th>
              <th className="p-3 text-left">Paid at</th>
              <th className="p-3 text-left">Total</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-left">Action</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="p-4 text-center">
                  Loading...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan="7" className="p-4 text-center">
                  No Data
                </td>
              </tr>
            ) : (
              rows.map((item) => (
                <tr
                  key={item.id}
                  className="border-t border-slate-200 dark:border-slate-700"
                >
                  <td className="p-3">{item.id}</td>
                  <td className="p-3">{item.customer_id}</td>
                  <td className="p-3">{item.no_invoice}</td>
                  <td className="p-3">{item.date}</td>
                  <td className="p-3">{item.date_paid}</td>
                  <td className="p-3">{Number(item.total).toLocaleString()}</td>
                  <td className="p-3">{item.status}</td>
                  <td className="p-3">
                    <button
                      onClick={() => del(item.id)}
                      className="inline-flex items-center gap-2 px-3 py-2 text-white bg-red-600 rounded-xl hover:bg-red-700"
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p>Total: {meta.total_data}</p>

        <div className="flex items-center gap-2">
          <button
            disabled={page <= 1}
            onClick={() => setPage((prev) => prev - 1)}
            className="px-3 py-2 border rounded-xl disabled:opacity-50"
          >
            Prev
          </button>

          <span>
            Page {page} / {meta.total_pages}
          </span>

          <button
            disabled={page >= meta.total_pages}
            onClick={() => setPage((prev) => prev + 1)}
            className="px-3 py-2 border rounded-xl disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
